// #region Imports
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { scrollShadows } from "_shared/_views/scroll-shadows";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleTeamMemberPickerField from "_shared/fee-schedules/categories/_views/fee-schedule-team-member-picker-field";
import {
  focusRequestFor,
  type FeeScheduleCategoryEditorFocus,
  useFocusWhenAsked,
} from "_shared/fee-schedules/categories/_views/fee-schedule-category-editor-focus";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import {
  FeeScheduleCategoryType,
  feeScheduleCategoryTypeLabel,
} from "_shared/fee-schedules/categories/types/fee-schedule-category-type";
import Text from "_shared/text/text";
import { useRef, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedFeeSchedule } from "work/masked-fields";
import FeeRedlineInput from "work/proposals/redlining/fee-schedule-redline/fee-redline/_views/fee-redline-input";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import TextRedlineInput from "work/proposals/redlining/field-redlines/_views/text-redline-input";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
// #endregion

// #region Styled Components
const FormContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
}));
// The fee and its "Let … set charge?" box, as one group a page can ask to focus (#6097). It draws no box
// of its own, so the two stay children of the form's own column and keep its spacing.
const FeeFields = styled("div")(() => ({
  display: "contents",
}));
// Update and Cancel stay put under the fields, which scroll between them and the title. A phone draws
// no scrollbar, so the box shadows the edge that has more past it (#7004).
const FormFields = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: theme.spacing(1),
  minHeight: 0,
  overflowY: "auto",
  ...scrollShadows(theme),
}));
/**
 * The grid's own text redline, drawn as a form field. Its natural floor is the 20rem the details tab
 * lays it out at, which is wider than this panel is at its narrowest, so here it takes the panel's
 * width instead. Doubled to outrank the component's own class.
 */
const PanelTextRedline = styled(TextRedlineInput)(() => ({
  "&&": {
    minWidth: 0,
    width: "100%",
  },
})) as typeof TextRedlineInput;
/**
 * Fee or expense, chosen the way the plain editor chooses it (#6882). Below it, what the type was
 * before this negotiation changed it, so a change the other side asked for is not silent here.
 */
const TypeFieldset = styled(FormControl)(() => ({
  width: "100%",
})) as typeof FormControl;
const TypeLegend = styled(FormLabel)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightMedium,
  "&.Mui-focused": {
    color: theme.palette.text.primary,
  },
})) as typeof FormLabel;
const TYPE_OPTIONS = [FeeScheduleCategoryType.Fee, FeeScheduleCategoryType.Expense] as const;
const MaskedFee = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: "100%",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  flexShrink: 0,
  marginTop: "auto",
  paddingTop: theme.spacing(3),
}));
const ButtonTooltipTarget = styled("span")(() => ({
  display: "grid",
}));
// #endregion

// #region Types
type FeeScheduleCategoryEntry = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
type CategoryField = "name" | "description" | "fee";
type CategoryChanges = Partial<{
  name: FeeScheduleCategoryName;
  description: FeeScheduleCategoryDescription;
  type: FeeScheduleCategoryType;
  fee: Fee | null;
  teamMember: EntityMember | null;
}>;

type FeeScheduleCategoryRedlineFormProps = {
  /** The row's redline, as the grid draws it. The form edits a copy and hands back the category. */
  categoryRedline: FeeScheduleCategoryRedline;
  /** The schedule the category is in, so a name another category already has cannot be saved. */
  selectedCategories?: FeeScheduleCategoryEntry[] | MaskedFeeSchedule;
  negotiationId?: string;
  /** Nothing may be changed: every field draws its redline and nothing else, and Update is disabled. */
  readOnly?: boolean;
  onBrowseForTeamMember?: () => Promise<EntityMember | undefined>;
  /** The vendor team as the redline has it now, offered before the full picker (#5798). */
  teamRoster?: FeeScheduleTeamRoster;
  /**
   * Asks for a field to take the focus: the Team Member or the Fee, and each new request is one more
   * ask (#6097). A page passes one when the reader pressed an issue about that part of the rate.
   */
  focus?: FeeScheduleCategoryEditorFocus;
  onCancel?: () => void;
  onCategoryUpdated?: (originalName: FeeScheduleCategoryName | null, category: FeeScheduleCategoryEntry) => void;
};
// #endregion

/**
 * Edits one category of an active proposal's fee schedule, with the redline of every field in view
 * (#5570).
 *
 * Each field is the one the fee schedule grid draws for it — `TextRedlineInput` for the name and
 * description, `FeeRedlineInput` for the fee — so a field the counterparty changed
 * reads exactly as its cell does: what was accepted struck through, what they ask for inserted, and
 * who asked. A field nobody changed is its plain value. Choosing a field opens it for editing, as
 * choosing a cell used to.
 *
 * The edits are made to a copy of the row's redline, which is what keeps the markup truthful while the
 * category is being worked on: each field shows how the category would read if it were saved now.
 * Nothing reaches the proposal until Update, which hands back the category as the copy leaves it. The
 * page records that through `FeeScheduleRedline.reviseEntryById`, which classifies the resolution
 * decisions at save time exactly as it did for the plain form.
 */
export default function FeeScheduleCategoryRedlineForm(props: Readonly<FeeScheduleCategoryRedlineFormProps>) {
  const {
    categoryRedline,
    selectedCategories,
    negotiationId,
    readOnly,
    onBrowseForTeamMember,
    teamRoster,
    focus,
    onCancel,
    onCategoryUpdated,
  } = props;

  const session = useSession();
  const feeFieldsRef = useRef<HTMLDivElement>(null);
  useFocusWhenAsked(feeFieldsRef, focusRequestFor(focus, "fee"));
  const [draftRedline, setDraftRedline] = useState(categoryRedline);
  // A fee taken back from the other side with no amount to take back (#6627), counted so that each
  // untick focuses the empty amount again. The draft still has no fee until somebody enters one, so
  // nothing is saved as $0.00 that nobody typed.
  const [feeEntryRequest, setFeeEntryRequest] = useState<number>();
  // The fields whose inputs hold a value they cannot commit (#6624). The draft still has each one's
  // last committed value, so Update would save that and drop what is on screen: it waits for them.
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<CategoryField, string>>>({});
  useFocusWhenAsked(feeFieldsRef, feeEntryRequest);

  const openedCategory = standingCategory(categoryRedline);
  const draftCategory = standingCategory(draftRedline);

  // #region Functions
  function reviseDraft(changes: CategoryChanges) {
    if (!draftCategory) return;
    setDraftRedline(draftRedline.edit(categoryWithChanges(draftCategory, changes)));
  }

  function handleTextRedlineChange<T extends Text>(
    field: "name" | "description",
    newRedline: FieldRedline<T>,
  ) {
    const entry = newRedline.currentEntry;
    // A name or a description cannot be emptied: the category would no longer be one.
    if (!entry?.value.trim()) return;
    if (field === "name") reviseDraft({ name: new FeeScheduleCategoryName(entry.value.trim()) });
    else reviseDraft({ description: new FeeScheduleCategoryDescription(entry.value.trim()) });
  }

  function handleFieldErrorChange(field: CategoryField, error: string | undefined) {
    setFieldErrors((errors) => (errors[field] === error ? errors : { ...errors, [field]: error }));
  }

  function handleDeferredChanged(deferred: boolean) {
    setFeeEntryRequest(undefined);
    // A fee handed to the other side has no inputs left to hold an error.
    handleFieldErrorChange("fee", undefined);
    if (deferred) {
      reviseDraft({ fee: null });
      return;
    }
    // Taking the fee back picks up the last amount anybody put on it. A fee that never had one opens
    // empty, and stays unsaved until it is entered: zero is an amount somebody has to mean.
    const fee = lastKnownFee(categoryRedline);
    if (!fee) {
      setFeeEntryRequest((request) => (request ?? 0) + 1);
      return;
    }
    reviseDraft({ fee });
  }

  function handleFeeChange(fee: Fee) {
    setFeeEntryRequest(undefined);
    reviseDraft({ fee });
  }

  function nameIsTakenInThisFeeSchedule(): boolean {
    if (!draftCategory || Masked.isMasked(selectedCategories)) return false;
    return selectedCategories?.some((category) =>
      !category.id.isEqualTo(draftCategory.id) && category.name.isEqualTo(draftCategory.name),
    ) ?? false;
  }
  // #endregion

  // #region Render
  if (!draftCategory || !openedCategory) return null;

  const isMaskedCurrency = draftCategory instanceof FeeScheduleCategoryMaskedCurrency;
  const draftFee = isMaskedCurrency ? undefined : draftCategory.fee;
  const isEnteringFee = feeEntryRequest !== undefined;
  const isDeferred = !isMaskedCurrency && draftFee === null && !isEnteringFee;
  // The staffing rule the plain form holds (`EditFeeScheduleCategory`): a rate that bills somebody's
  // time names who, and only a rate with a price and a unit has anybody's time to bill.
  // A fee this reader cannot see may well be one, so its staffing stays offered.
  const teamMemberApplies = isMaskedCurrency || Boolean(draftFee?.unit);
  const teamMemberIsMasked = draftCategory.teamMemberIsMasked;
  const showsTeamMember = Boolean(onBrowseForTeamMember) && (teamMemberApplies || teamMemberIsMasked);
  // An hourly fee names who bills it by the time the proposal is approved or hired, and the field says
  // so. It does not stop the save: during a negotiation the rate can be agreed before the person is,
  // and the issues panel warns until somebody is named (#6605, #6883). An expense bills nobody's time.
  const teamMemberIsRequired =
    !teamMemberIsMasked &&
    draftCategory.type === FeeScheduleCategoryType.Fee &&
    Boolean(draftFee && Fee.isHourlyUnit(draftFee.unit));
  const savedCategory = showsTeamMember || !draftCategory.teamMember
    ? draftCategory
    : categoryWithChanges(draftCategory, { teamMember: null });

  const nameIsTaken = nameIsTakenInThisFeeSchedule();
  const hasChanges = !savedCategory.isEqualTo(openedCategory);
  const hasFieldError = Object.values(fieldErrors).some(Boolean);
  const saveDisabled =
    readOnly || nameIsTaken || isEnteringFee || hasFieldError || !hasChanges;
  let saveDisabledTooltip = "";
  if (hasFieldError) saveDisabledTooltip = "Correct the field marked in red, or press Escape in it to cancel.";
  else if (isEnteringFee) saveDisabledTooltip = "Enter the fee, or let the other side set it.";
  else if (nameIsTaken) saveDisabledTooltip = "This category already exists in this fee schedule.";

  // The type both parties last agreed on. The editor opens on the counterparty's entry, so comparing
  // with that hid an incoming Fee-to-Expense change, and said "Was Expense" once it was put back to
  // Fee. A category nobody has agreed on yet compares with the entry it opened on.
  const acceptedTypeLabel =
    categoryRedline.typeRedline?.originalEntry?.value ?? feeScheduleCategoryTypeLabel(openedCategory.type);

  const otherParty = session.context?.viewingAsVendor ? "client" : "vendor";
  const textRedlineProps = {
    readOnly,
    negotiationId,
    showPlaceholder: true,
    hideAcceptRejectButtons: true,
    hideUndoButton: true,
    hideFieldHistory: true,
    showResolvedHelperText: false,
  } as const;

  return (
    <FormContainer>
      <FormFields>
        {draftRedline.nameRedline && (
          <PanelTextRedline
            {...textRedlineProps}
            label="Category"
            required
            bold
            textRedline={draftRedline.nameRedline}
            fieldConstructor={FeeScheduleCategoryName}
            onTextFieldRedlineChange={(newRedline) => handleTextRedlineChange("name", newRedline)}
            onDraftErrorChange={(error) => handleFieldErrorChange("name", error)}
          />
        )}
        {draftRedline.descriptionRedline && (
          <PanelTextRedline
            {...textRedlineProps}
            label="Description Of Work"
            required
            multiline
            textRedline={draftRedline.descriptionRedline}
            fieldConstructor={FeeScheduleCategoryDescription}
            onTextFieldRedlineChange={(newRedline) => handleTextRedlineChange("description", newRedline)}
            onDraftErrorChange={(error) => handleFieldErrorChange("description", error)}
          />
        )}
        <TypeFieldset component="fieldset" required disabled={readOnly}>
          <TypeLegend component="legend">Type</TypeLegend>
          <RadioGroup
            row
            name="fee-schedule-category-redline-type"
            value={draftCategory.type}
            onChange={(event) => reviseDraft({ type: event.target.value as FeeScheduleCategoryType })}
          >
            {TYPE_OPTIONS.map((type) => (
              <FormControlLabel
                key={type}
                value={type}
                label={feeScheduleCategoryTypeLabel(type)}
                control={<Radio color="primary" />}
              />
            ))}
          </RadioGroup>
          {acceptedTypeLabel !== feeScheduleCategoryTypeLabel(draftCategory.type) && (
            <FormHelperText>Was {acceptedTypeLabel}</FormHelperText>
          )}
        </TypeFieldset>
        {isMaskedCurrency ? (
          // Somebody else's rate, as the grid's cell says it: there is one, and it is not this reader's to see.
          <MaskedFee label="Fee" value="Masked" disabled slotProps={{ inputLabel: { shrink: true } }} />
        ) : (
          <FeeFields ref={feeFieldsRef}>
            {!isDeferred && (
              <FeeRedlineInput
                label="Fee"
                categoryRedline={draftRedline}
                readOnly={readOnly}
                showTextInput={isEnteringFee}
                onFeeChange={handleFeeChange}
                onDraftErrorChange={(error) => handleFieldErrorChange("fee", error)}
              />
            )}
            <FormControlLabel
              label={`Let ${otherParty} set charge?`}
              control={
                <Checkbox
                  color="primary"
                  checked={isDeferred}
                  disabled={readOnly}
                  onChange={(event) => handleDeferredChanged(event.target.checked)}
                />
              }
            />
          </FeeFields>
        )}
        {showsTeamMember && onBrowseForTeamMember && (
          <FeeScheduleTeamMemberPickerField
            teamMember={draftCategory.teamMember ?? null}
            isMasked={teamMemberIsMasked}
            disabled={readOnly || teamMemberIsMasked}
            required={teamMemberIsRequired}
            roster={teamRoster}
            openedTeamMember={openedCategory.teamMember ?? null}
            onBrowse={onBrowseForTeamMember}
            onChange={(teamMember) => reviseDraft({ teamMember })}
            focusRequest={focusRequestFor(focus, "teamMember")}
          />
        )}
      </FormFields>
      <ButtonContainer>
        {/*
          "Update", and not "Save", with no save icon beside it: this button hands the category back to
          the fee schedule. It does not write the proposal; the page's own Save does that (#5657).
        */}
        <Tooltip title={saveDisabledTooltip}>
          <ButtonTooltipTarget>
            <Button
              color="primary"
              variant="contained"
              disabled={saveDisabled}
              onClick={() => {
                // Disabled already, but a field's blur can report its error in the same click.
                if (saveDisabled) return;
                onCategoryUpdated?.(openedCategory.name, savedCategory);
              }}
            >
              Update
            </Button>
          </ButtonTooltipTarget>
        </Tooltip>
        {onCancel && (
          <Button color="primary" variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </ButtonContainer>
    </FormContainer>
  );
  // #endregion
}

/** The category a row's editors start from: this party's own entry, else the one asked for. */
function standingCategory(redline: FeeScheduleCategoryRedline): FeeScheduleCategoryEntry | null {
  if (redline.isRemoved) return null;
  return redline.currentEntry ?? redline.revisedEntry;
}

/** The most recent fee the category has carried, for a fee taken back from the other side. */
function lastKnownFee(redline: FeeScheduleCategoryRedline): Fee | null {
  for (const entry of [redline.currentEntry, redline.revisedEntry, redline.originalEntry]) {
    if (entry instanceof FeeScheduleCategory && entry.fee) return entry.fee;
  }
  return null;
}

function categoryWithChanges(category: FeeScheduleCategoryEntry, changes: CategoryChanges): FeeScheduleCategoryEntry {
  const params = {
    id: category.id,
    name: changes.name ?? category.name,
    description: changes.description ?? category.description,
    type: changes.type ?? category.type,
    teamMember: "teamMember" in changes ? changes.teamMember : category.teamMember,
    teamMemberIsMasked: category.teamMemberIsMasked,
    version: category.version,
  };
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return new FeeScheduleCategoryMaskedCurrency(params);
  return new FeeScheduleCategory({ ...params, fee: "fee" in changes ? changes.fee : category.fee });
}
